import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { miniGamesAPI } from '@/lib/api';
import { Gamepad2, Trophy, Play, TrendingUp } from 'lucide-react';

export default function MiniGamesPage() {
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGames();
  }, []);

  const fetchGames = async () => {
    try {
      setLoading(true);
      const response = await miniGamesAPI.getGames();
      setGames(response.data.games || []);
    } catch (error) {
      console.error('Error fetching games:', error);
    } finally {
      setLoading(false);
    }
  };

  const getColorClass = (color) => {
    const colors = {
      green: 'bg-green-500/10 border-green-500/50 hover:bg-green-500/20',
      blue: 'bg-blue-500/10 border-blue-500/50 hover:bg-blue-500/20',
      purple: 'bg-purple-500/10 border-purple-500/50 hover:bg-purple-500/20',
    };
    return colors[color] || 'bg-gray-500/10 border-gray-500/50 hover:bg-gray-500/20';
  };

  const getColorText = (color) => {
    const colors = {
      green: 'text-green-400',
      blue: 'text-blue-400',
      purple: 'text-purple-400',
    };
    return colors[color] || 'text-gray-400';
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Gamepad2 className="w-12 h-12 animate-bounce mx-auto mb-4 text-cyan-400" />
            <p className="text-gray-400">Games laden...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Gamepad2 className="w-10 h-10 text-cyan-400" />
          <h1 className="text-4xl font-bold">Mini-Games</h1>
        </div>
        <p className="text-gray-400 text-lg">
          Speel klassieke arcade games en competeer voor de hoogste score!
        </p>
      </div>

      {/* Games Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {games.map((game) => (
          <Card
            key={game.id}
            className={`border-2 transition-all cursor-pointer ${getColorClass(game.color)}`}
            onClick={() => navigate(`/mini-games/${game.id}`)}
          >
            <CardHeader>
              <div className="flex items-center justify-between mb-2">
                <span className="text-5xl">{game.icon}</span>
                <Badge variant="outline" className={getColorText(game.color)}>
                  {game.play_count} spellen
                </Badge>
              </div>
              <CardTitle className="text-2xl">{game.name}</CardTitle>
              <CardDescription className="text-base">
                {game.description}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* High Score */}
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Trophy className={`w-5 h-5 ${getColorText(game.color)}`} />
                    <span className="text-sm text-gray-400">High Score</span>
                  </div>
                  <span className={`text-xl font-bold ${getColorText(game.color)}`}>
                    {game.high_score || 0}
                  </span>
                </div>

                {/* Play Button */}
                <Button
                  className={`w-full ${
                    game.color === 'green'
                      ? 'bg-green-600 hover:bg-green-700'
                      : game.color === 'blue'
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-purple-600 hover:bg-purple-700'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/mini-games/${game.id}`);
                  }}
                >
                  <Play className="w-4 h-4 mr-2" />
                  Spelen
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Info Section */}
      <Card className="mt-8 border-cyan-500/30 bg-cyan-500/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-cyan-400" />
            Over Mini-Games
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-gray-400">
            <p>• Speel klassieke arcade games rechtstreeks in je browser</p>
            <p>• Competeer met andere spelers voor de hoogste score</p>
            <p>• Elke game houdt een leaderboard bij met de top 10 scores</p>
            <p>• Je persoonlijke beste score wordt automatisch opgeslagen</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
